import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Alert,
  useMediaQuery,
  Theme,
} from '@mui/material';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '../components/DashboardLayout';
import { useChat } from '../hooks/useChat';

// Імпортуємо компоненти з нової папки /components/chat/
import { ConversationsList } from '../components/chat/ConversationsList';
import { ChatWindow } from '../components/chat/ChatWindow';
import { CreateConversationDialog } from '../components/chat/CreateConversationDialog';

// Основний компонент сторінки чату, тепер він виступає як контейнер
const ChatPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Хук викликається один раз тут, на найвищому рівні сторінки
  const chat = useChat();
  const { error, clearChatError, activeConversationId, setCurrentConversation } = chat;

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // Ефект для обробки URL параметру id
  useEffect(() => {
    if (id) {
      const conversationId = parseInt(id, 10);
      if (!isNaN(conversationId) && conversationId !== activeConversationId) {
        console.log(`[ChatPage] Setting active conversation from URL: ${conversationId}`);
        setCurrentConversation(conversationId);
      }
    } else {
      // Якщо ID немає в URL, але є активна розмова в стейті - скидаємо її.
      // Це важливо для коректної роботи кнопки "Назад" та навігації на /chat
      if (activeConversationId !== null) {
        console.log('[ChatPage] Clearing active conversation (no ID in URL)');
        setCurrentConversation(null);
      }
    }
  }, [id, activeConversationId, setCurrentConversation]);

  return (
    <DashboardLayout data-i-doc={PageIndices.CHAT}>
      <Box sx={{ height: 'calc(100vh - 112px)' }}>
        {error && (
          <Alert
            severity="error"
            onClose={clearChatError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Paper elevation={1} sx={{ height: '100%', display: 'flex' }}>
          {isMobile ? (
            // Mobile view: show one component at a time
            <Box sx={{ width: '100%', height: '100%' }}>
              {activeConversationId ? (
                <ChatWindow chat={chat} />
              ) : (
                <ConversationsList
                  onCreateConversation={() => setCreateDialogOpen(true)}
                  chat={chat}
                />
              )}
            </Box>
          ) : (
            // Desktop view: show both components
            <Grid container sx={{ height: '100%' }}>
              {/* Список розмов */}
              <Grid item xs={12} md={4} sx={{ borderRight: { md: '1px solid' }, borderColor: 'divider', height: '100%' }}>
                <ConversationsList
                  onCreateConversation={() => setCreateDialogOpen(true)}
                  chat={chat}
                />
              </Grid>

              {/* Вікно чату */}
              <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                <ChatWindow chat={chat} />
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Діалогове вікно */}
        <CreateConversationDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          chat={chat}
        />
      </Box>
    </DashboardLayout>
  );
};

export default ChatPage;
